import { useCallback, useEffect, useRef, useState } from "react";

// Lets any fixed-position element (button, card, widget) be dragged around the screen
// on desktop (mouse) and mobile (touch/pen). A short tap/click (< DRAG_START px) still
// works as a click; a drag of more than a few pixels moves the element and swallows the click.
// The position is kept in sessionStorage for this tab, and is pulled back inside the screen if the window shrinks.
//   const drag = useDraggable("oz_aidog_position");
//   <button ref={drag.ref} style={drag.style} {...drag.handlers}>…</button>
const PHONE = "(max-width: 767.98px)";
const MARGIN = 8; // px kept clear of the screen edges
const DRAG_START = 6; // px of movement before a press counts as a drag

const readPosition = (key) => {
    try {
        const saved = JSON.parse(sessionStorage.getItem(key));
        return saved && Number.isFinite(saved.x) && Number.isFinite(saved.y) ? saved : null;
    } catch {
        return null;
    }
};

const clamp = (value, max, m = MARGIN) => Math.min(Math.max(value, m), Math.max(m, max - m));

export default function useDraggable(storageKey, options = {}) {
    const { allDevices = true, margin = MARGIN } = options;
    const [position, setPosition] = useState(() => readPosition(storageKey));
    const [isDragging, setIsDragging] = useState(false);
    const drag = useRef(null);
    const moved = useRef(false);
    const element = useRef(null);

    const setRef = useCallback((node) => {
        element.current = node;
    }, []);

    // Keep a saved position on screen after rotation or resizing
    useEffect(() => {
        const fit = () => {
            if (!allDevices && !window.matchMedia(PHONE).matches) return setPosition(null);
            const el = element.current;
            if (!el) return;
            setPosition((p) => {
                if (!p) return null;
                const maxX = Math.max(margin, window.innerWidth - (el.offsetWidth || 160));
                const maxY = Math.max(margin, window.innerHeight - (el.offsetHeight || 60));
                return { x: clamp(p.x, maxX, margin), y: clamp(p.y, maxY, margin) };
            });
        };
        fit();
        window.addEventListener("resize", fit);
        window.addEventListener("orientationchange", fit);
        return () => {
            window.removeEventListener("resize", fit);
            window.removeEventListener("orientationchange", fit);
        };
    }, [allDevices, margin]);

    const onPointerDown = useCallback((event) => {
        if (event.button !== undefined && event.button !== 0) return;
        if (!allDevices && !window.matchMedia(PHONE).matches) return;
        const rect = event.currentTarget.getBoundingClientRect();
        element.current = event.currentTarget;
        drag.current = {
            startX: event.clientX,
            startY: event.clientY,
            offsetX: event.clientX - rect.left,
            offsetY: event.clientY - rect.top,
            width: rect.width,
            height: rect.height,
        };
        moved.current = false;
    }, [allDevices]);

    const onPointerMove = useCallback((event) => {
        const d = drag.current;
        if (!d) return;
        if (!moved.current) {
            if (Math.hypot(event.clientX - d.startX, event.clientY - d.startY) < DRAG_START) return;
            moved.current = true;
            setIsDragging(true);
            try {
                event.currentTarget.setPointerCapture?.(event.pointerId);
            } catch {}
        }
        const maxX = Math.max(margin, window.innerWidth - d.width);
        const maxY = Math.max(margin, window.innerHeight - d.height);
        setPosition({
            x: clamp(event.clientX - d.offsetX, maxX, margin),
            y: clamp(event.clientY - d.offsetY, maxY, margin),
        });
    }, [margin]);

    const onPointerUp = useCallback((event) => {
        if (!drag.current) return;
        try {
            if (event && event.currentTarget && event.pointerId) {
                event.currentTarget.releasePointerCapture?.(event.pointerId);
            }
        } catch {}
        drag.current = null;
        setIsDragging(false);
        if (!moved.current) return;
        setPosition((p) => {
            try {
                if (p) sessionStorage.setItem(storageKey, JSON.stringify(p));
            } catch {
                /* private mode: only this page remembers */
            }
            return p;
        });
    }, [storageKey]);

    // A drag ends with a click event; it must not open whatever the button opens
    const onClickCapture = useCallback((event) => {
        if (!moved.current) return;
        moved.current = false;
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const onDragStart = useCallback((event) => {
        event.preventDefault();
    }, []);

    return {
        ref: setRef,
        style: position ? { left: position.x, top: position.y, right: "auto", bottom: "auto" } : undefined,
        handlers: {
            onPointerDown,
            onPointerMove,
            onPointerUp,
            onPointerCancel: onPointerUp,
            onClickCapture,
            onDragStart,
        },
        isMoved: Boolean(position),
        isDragging,
    };
}
